import { apiService } from '@/shared/services/api';
import { endpoints } from '@/shared/services/endpoints';
import type { ApiResponse } from '@/shared/types/api';
import type { DailyMetric } from '@/shared/types/entities';
import type {
  AnalyticsDashboardData,
  CampaignAnalytics,
  TopCampaign,
} from '../types/analytics.types';

interface DashboardApiResponse {
  totalSpendCents: number;
  totalRevenueCents: number;
  avgRoas: number;
  totalImpressions: number;
  totalClicks: number;
  totalConversions: number;
  activeCampaigns: number;
  totalCampaigns: number;
  topCampaigns: TopCampaign[];
}

export async function fetchDashboardAnalytics(
  organizationId: string,
  dateRange?: { startDate: string; endDate: string },
): Promise<AnalyticsDashboardData> {
  const params: Record<string, string> = {};
  if (dateRange?.startDate) {
    params.startDate = dateRange.startDate;
  }
  if (dateRange?.endDate) {
    params.endDate = dateRange.endDate;
  }

  const { data: raw } = await apiService.get<{ data: DashboardApiResponse } | DashboardApiResponse>(
    endpoints.analytics.dashboard(organizationId),
    { params },
  );

  // Handle both wrapped ({ data: ... }) and unwrapped response shapes
  const data =
    'data' in raw &&
    typeof raw.data === 'object' &&
    raw.data !== null &&
    'totalSpendCents' in raw.data
      ? (raw.data as DashboardApiResponse)
      : (raw as DashboardApiResponse);

  const totalImpressions = data.totalImpressions ?? 0;
  const totalClicks = data.totalClicks ?? 0;

  return {
    totalSpendCents: data.totalSpendCents ?? 0,
    totalRevenueCents: data.totalRevenueCents ?? 0,
    averageRoas: data.avgRoas ?? 0,
    averageCtr: totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0,
    ticketsSold: 0,
    totalImpressions,
    totalClicks,
    totalConversions: data.totalConversions ?? 0,
    activeCampaigns: data.activeCampaigns ?? 0,
    totalCampaigns: data.totalCampaigns ?? 0,
    dailyMetrics: [],
    platformBreakdown: [],
    topCampaigns: data.topCampaigns ?? [],
  };
}

/** Raw `GET /analytics/campaigns/:id` body, before the envelope is unwrapped. */
interface CampaignAnalyticsApiResponse {
  campaignId?: string;
  campaignName?: string;
  platform?: string;
  totals?: Partial<CampaignAnalytics['totals']>;
  timeSeries?: DailyMetric[];
}

const EMPTY_TOTALS: CampaignAnalytics['totals'] = {
  impressions: 0,
  clicks: 0,
  conversions: 0,
  spendCents: 0,
  revenueCents: 0,
  reach: 0,
  ctr: 0,
  cpc: 0,
  roas: 0,
};

export async function fetchCampaignAnalytics(
  id: string,
  dateRange?: { startDate: string; endDate: string },
): Promise<CampaignAnalytics> {
  const params: Record<string, string> = {};
  if (dateRange?.startDate) {
    params.startDate = dateRange.startDate;
  }
  if (dateRange?.endDate) {
    params.endDate = dateRange.endDate;
  }

  // The backend wraps every successful response in `{ data }` (a global
  // TransformInterceptor), so this has to unwrap like every other call does.
  // Returning the envelope typed as CampaignAnalytics left `totals` undefined,
  // and the dashboard crashed the moment a campaign was selected.
  const { data } = await apiService.get<ApiResponse<CampaignAnalyticsApiResponse>>(
    endpoints.analytics.campaign(id),
    { params },
  );
  const payload = data.data ?? {};
  const totals = { ...EMPTY_TOTALS, ...payload.totals };

  return {
    campaignId: payload.campaignId ?? id,
    campaignName: payload.campaignName ?? '',
    platform: payload.platform ?? '',
    totals: {
      ...totals,
      // The backend reports CTR as a fraction (clicks / impressions); every
      // other CTR on the dashboard is already a 0-100 percentage, and the
      // formatter only appends '%'. Without this a 2.5% CTR rendered as 0.03%.
      ctr: totals.ctr * 100,
    },
    timeSeries: payload.timeSeries ?? [],
  };
}
